import { memo, useMemo } from 'react';

import Colors from '@dynatrace/strato-design-tokens/colors';

import { useChartLayoutLoading } from '../../../core/components/chart-layout/hooks/useChartLayoutLoading.js';
import { useChartLayoutOrientation } from '../../../core/components/chart-layout/hooks/useChartLayoutOrientation.js';
import { getLegendOrientation } from '../../../core/components/chart-layout/utils/orientation.js';
import { reduceExploreCancelAll } from '../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import { ChartLegend } from '../../../core/components/legend/categorical/ChartLegend.js';
import { useLegendSeriesActions } from '../../../core/components/legend/categorical/hooks/useLegendSeriesActions.js';
import { LegendItem } from '../../../core/components/legend/categorical/types/legend.js';
import { singleLineName } from '../../../core/utils/single-line-name.js';
import { useSetState } from '../../hooks/store/state-setters.js';
import { useHistogramData } from '../../hooks/useHistogramData.js';
import { useHistogramTruncationMode } from '../../hooks/useHistogramTruncationMode.js';
import { safeGet } from '../../providers/HistogramDataProvider.js';
import { HistogramSeries, SeriesMetadata } from '../../types/histogram.js';

const createItemData = (
  series: HistogramSeries[],
  seriesMap: WeakMap<object, SeriesMetadata>,
): LegendItem[] => {
  const result: LegendItem[] = [];

  for (let index = 0; index < series.length; index++) {
    const singleSeries = series[index];
    const seriesMetadata = safeGet(seriesMap, singleSeries);

    result.push({
      id: seriesMetadata.id ?? `item-${index}`,
      color: seriesMetadata.color ?? Colors.Text.Neutral.Default,
      name: singleLineName(singleSeries.name) ?? seriesMetadata.id,
    });
  }

  return result;
};

/**
 * HistogramChartLegend component.
 */
export const HistogramChartLegend = memo(() => {
  const { series, seriesMap } = useHistogramData();
  const truncationMode = useHistogramTruncationMode();
  const setState = useSetState();
  const { orientation: layoutOrientation } = useChartLayoutOrientation();
  const legendOrientation = getLegendOrientation(layoutOrientation);
  const isLoading = useChartLayoutLoading();

  const legendItems = useMemo(
    () => createItemData(series, seriesMap),
    [series, seriesMap],
  );

  const updateHighlightedSeries = (item?: LegendItem) => {
    setState((prev) => ({
      ...prev,
      highlightedSeriesId: item?.id,
    }));
  };

  const updateVisibleSeries = (visibleSeries: LegendItem[]) => {
    const visibleSeriesSet = new Set(visibleSeries.map((s) => s.id));

    setState((prev) =>
      reduceExploreCancelAll({
        ...prev,
        filteredSeriesIds: visibleSeriesSet,
      }),
    );
  };

  const legendActions = useLegendSeriesActions(
    series,
    (s) => safeGet(seriesMap, s).id,
  );

  return (
    <ChartLegend
      items={legendItems}
      orientation={legendOrientation}
      interactionsDisabled={isLoading}
      onHover={updateHighlightedSeries}
      onClick={updateVisibleSeries}
      truncationMode={truncationMode}
      onSeriesAction={legendActions}
    />
  );
});

HistogramChartLegend['displayName'] = 'HistogramChartLegend';
